import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Paper,
  Stack,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { CachedValue, equipDataCacheInstance, formatCachedValue, ReqResult } from "@/services/EquipDataCache";
import { validKeyType } from "@/services/object-types-hydrators-map";

type Entry = {
  req: validKeyType;
  result: CachedValue;
  year?: number;
  periodType?: string;
};

type GroupedType = Map<string, Map<string, Map<string, Entry[]>>>;

export default function BatchCache() {
  const [lastRefreshed, setLastRefreshed] = useState<string>("");
  const [grouped, setGrouped] = useState<GroupedType>(new Map());

  useEffect(() => {
    const refresh = () => refreshFromCache();
    const unsub = equipDataCacheInstance.Subscribe(refresh);
    refreshFromCache();
    return () => {
      //UNSUB
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function refreshFromCache(): void {
    const exposed = equipDataCacheInstance.Expose();
    const cache = exposed.map as Map<validKeyType, ReqResult>;

    const next: GroupedType = new Map();

    for (const [req, value] of cache) {
      const rt = value.requestType;
      const ric = req.companygroup;
      const formula = req.formula;
      const yr =
        typeof (req as any).yearOrRollingPeriod === "number"
          ? ((req as any).yearOrRollingPeriod as number)
          : undefined;

      const periodType =
        typeof (req as any).periodtype === "string"
          ? ((req as any).periodtype as string)
          : undefined;

      if (!next.has(rt)) next.set(rt, new Map());
      const byType = next.get(rt)!;

      if (!byType.has(ric)) byType.set(ric, new Map());
      const byRic = byType.get(ric)!;

      if (!byRic.has(formula)) byRic.set(formula, []);
      byRic.get(formula)!.push({ req, result: value.result, year: yr, periodType: periodType });
    }

    setGrouped(next);
    setLastRefreshed(exposed.lastRefreshed);
  }

  const countRic = (formulas: Map<string, Entry[]>) =>
    [...formulas.values()].reduce((n, arr) => n + arr.length, 0);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        backgroundColor: "background.paper",
      }}
    >
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
        Cache Refreshed: {lastRefreshed}
      </Typography>

      <Stack spacing={1}>
        {[...grouped.entries()].map(([requestType, companies]) => (
          <Accordion
            key={requestType}
            disableGutters
            elevation={0}
            sx={{
              "&:before": {
                display: "none",
              },
              boxShadow: "none",
              border: "none",
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight={600}>
                {requestType}
              </Typography>
              <Chip
                size="small"
                sx={{ ml: 1 }}
                label={`${[...companies.values()].reduce((acc, f) => acc + countRic(f), 0)} items`}
              />
            </AccordionSummary>

            <AccordionDetails>
              {[...companies.entries()].map(([ric, formulas]) => (
                <Accordion
                  key={ric}
                  disableGutters
                  elevation={0}
                  sx={{
                    "&:before": {
                      display: "none",
                    },
                    boxShadow: "none",
                    border: "none",
                    ml: 1,
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body1" fontWeight={500}>
                      {ric}
                    </Typography>
                    <Chip size="small" sx={{ ml: 1 }} label={countRic(formulas)} />
                  </AccordionSummary>

                  <AccordionDetails>
                    <Stack spacing={1} sx={{ ml: 2 }}>
                      {[...formulas.entries()].map(([formula, entries]) =>
                        entries.length == 1 ? (
                          <Box
                            key={`${requestType}-${ric}-${formula}`}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              backgroundColor: "action.hover",
                            }}
                          >
                            {/* Left: optional year chip */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <Chip size="small" label={`${formula}`} />
                            </Box>

                            {/* Right: value */}
                            <Typography
                              variant="body2"
                              sx={{
                                color:
                                  entries[0].result === undefined
                                    ? "text.disabled"
                                    : "text.primary",
                                fontWeight: 500,
                              }}
                            >
                              {formatCachedValue(entries[0].result) ?? "—"}
                            </Typography>
                          </Box>
                        ) : (
                          <Box key={`${ric}-${formula}`}>
                            <Accordion key={`${ric}-${formula}`} disableGutters sx={{ ml: 1 }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
                                  {formula}
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Stack spacing={0.5}>
                                  {entries
                                    .sort((a, b) => a.year - b.year)
                                    .map(({ result, year, periodType }, i) => (
                                      <Box
                                        key={`${ric}-${formula}-${i}`}
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          px: 1,
                                          py: 0.5,
                                          borderRadius: 1,
                                          backgroundColor: "action.hover",
                                        }}
                                      >
                                        {/* Left: optional year chip */}
                                        <Box
                                          sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                                        >
                                          <Chip size="small" label={`${year} ${periodType}`} />
                                        </Box>

                                        {/* Right: value */}
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color:
                                              result === undefined
                                                ? "text.disabled"
                                                : "text.primary",
                                            fontWeight: 500,
                                          }}
                                        >
                                          {entries[0].result ?? "—"}
                                        </Typography>
                                      </Box>
                                    ))}
                                </Stack>
                              </AccordionDetails>
                            </Accordion>
                          </Box>
                        )
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </AccordionDetails>

            <Divider />
          </Accordion>
        ))}
      </Stack>
    </Paper>
  );
}
